import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, Loader2, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { lmError } from './lmApi';
import { GhostButton, Pill, PrimaryButton } from './LmBits';
import { MATCH_LABELS, blockLabel, labelOf } from './lmColumns';

/** A field's List Management label (#1014); an unknown key reads with spaces for underscores. */
const fieldName = (field, fieldLabels) => {
    const label = labelOf(field, fieldLabels);
    return label === field ? field.replace(/_/g, ' ') : label;
};

/**
 * Resolve match, one row at a time (#772 QA). For each selected Staging row:
 * every CRM candidate with its match evidence; the Admin picks one, or Create
 * new, or Skip. Picking a candidate shows a field-by-field comparison — CRM
 * (current) beside Import (staged) — computed by the server with Commit's own
 * rules, and where the import's write policy would write a value the Admin
 * chooses which one is kept. A summary of what Commit will write comes before
 * Apply. Applying only changes the row's decision and staged values: nothing
 * reaches the CRM until Commit.
 */
export const RESOLVE_MAX_ROWS = 50;

// The match ladder's evidence codes (services/list_management/matching.py).
const EVIDENCE = {
    email: 'Same email', domain: 'Same domain', name: 'Same company name', name_and_company: 'Same name at the matched company',
    name_only: 'Same name, another company', pitchbook_id: 'Same PitchBook ID', pitchbook_person_id: 'Same PitchBook person ID',
    multiple_email_matches: 'Several records share this email', multiple_domain_matches: 'Several companies share this domain',
    multiple_name_matches: 'Several records share this name', multiple_pitchbook_matches: 'Several records share this PitchBook ID',
    domain_conflict: 'Same name, different domain', duplicate_in_import: 'Duplicate inside this import',
    several_companies_in_import: 'Several companies in this import share the name',
};
const evidenceLabel = (e) => EVIDENCE[e] || String(e || '').replace(/_/g, ' ');
const show = (v) => (v == null || v === '' ? '—' : Array.isArray(v) ? v.join(', ') : String(v));
const asStaged = (v) => (v == null || v === '' ? null : Array.isArray(v) ? v.join(', ') : String(v));

function Comparison({ comparison, choices, setChoice, fieldLabels }) {
    if (!comparison) return null;
    const label = (f) => fieldName(f.field, fieldLabels);
    return (
        <table className="w-full border-collapse text-[12.5px]" data-testid="lm-resolve-compare">
            <thead>
                <tr className="text-left text-[11px] font-semibold tracking-[0.05em] text-muted-foreground">
                    <th scope="col" className="py-1.5 pr-2">FIELD</th>
                    <th scope="col" className="py-1.5 pr-2">CRM (CURRENT)</th>
                    <th scope="col" className="py-1.5 pr-2">IMPORT (STAGED)</th>
                    <th scope="col" className="py-1.5">KEEP</th>
                </tr>
            </thead>
            <tbody>
                {comparison.fields.map((f) => {
                    const name = `keep-${f.field}`;
                    return (
                        <tr key={f.field} className="border-t border-border/60 align-top" data-testid="lm-resolve-field">
                            <th scope="row" className="py-1.5 pr-2 font-medium text-ink-2 whitespace-nowrap">{label(f)}</th>
                            <td className="py-1.5 pr-2 text-foreground break-all">{show(f.crm)}</td>
                            <td className="py-1.5 pr-2 text-foreground break-all">{show(f.written)}</td>
                            <td className="py-1.5 whitespace-nowrap">
                                {f.same ? <span className="text-emerald-800 dark:text-good">Already the same</span>
                                    : f.would_write ? (
                                        <fieldset className="flex gap-3">
                                            <legend className="sr-only">Keep for {label(f)}</legend>
                                            <label className="inline-flex items-center gap-1">
                                                <input type="radio" name={name} checked={choices[f.field] !== 'crm'}
                                                       onChange={() => setChoice(f.field, 'import')} />Import
                                            </label>
                                            <label className="inline-flex items-center gap-1">
                                                <input type="radio" name={name} checked={choices[f.field] === 'crm'}
                                                       onChange={() => setChoice(f.field, 'crm')} />CRM
                                            </label>
                                        </fieldset>
                                    ) : (
                                        <span className="text-muted-foreground">
                                            CRM kept{f.never_overwrite ? ' (never overwritten)' : ' (fill blank only)'}
                                        </span>
                                    )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export default function LmResolveDialog({ open, onClose, api, rowIds, fieldLabels = {}, onChanged }) {
    const ids = useMemo(() => (rowIds || []).slice(0, RESOLVE_MAX_ROWS), [rowIds]);
    const [index, setIndex] = useState(0);
    const [detail, setDetail] = useState(null);
    const [loadError, setLoadError] = useState(null);
    const [decision, setDecision] = useState(null);  // candidate id | 'new' | 'skip'
    const [company, setCompany] = useState(null);    // company candidate id | 'new' | null (unchanged)
    const [comparison, setComparison] = useState(null);
    const [compareError, setCompareError] = useState(null);
    const [choices, setChoices] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [outcomes, setOutcomes] = useState({});    // row_id -> 'resolved' | message

    const rowId = ids[index];
    const row = detail?.row;
    const match = row?.match_full || {};
    const candidates = (match.candidates || []).filter((c) => c.kind === (row?.entity_type === 'Company' ? 'account' : 'contact'));
    const companyMatch = row?.entity_type === 'Contact' ? match.company : null;
    const companyCandidates = companyMatch?.candidates || [];

    useEffect(() => { if (open) { setIndex(0); setOutcomes({}); } }, [open, ids]);

    useEffect(() => {
        if (!open || !rowId) return undefined;
        let alive = true;
        setDetail(null); setLoadError(null); setComparison(null); setCompareError(null);
        setChoices({}); setError(null); setCompany(null);
        api.row(rowId).then((d) => {
            if (!alive) return;
            setDetail(d);
            const r = d.row;
            const cands = ((r.match_full || {}).candidates || []).filter((c) => c.kind === (r.entity_type === 'Company' ? 'account' : 'contact'));
            // Start from what is already decided; a lone candidate is pre-picked.
            const preset = r.match_decision === 'skip' ? 'skip'
                : r.decision_target_id || (cands.length === 1 ? cands[0].id : null);
            setDecision(preset || (cands.length ? null : 'new'));
        }).catch((err) => alive && setLoadError(lmError(err, 'Could not load this record.')));
        return () => { alive = false; };
    }, [open, api, rowId]);

    const candidateId = decision && decision !== 'new' && decision !== 'skip' ? decision : null;
    useEffect(() => {
        if (!candidateId || !rowId) { setComparison(null); return undefined; }
        let alive = true;
        setComparison(null); setCompareError(null); setChoices({});
        api.compareCandidate(rowId, candidateId)
            .then((c) => alive && setComparison(c))
            .catch((err) => alive && setCompareError(lmError(err, 'Could not compare with this record.')));
        return () => { alive = false; };
    }, [api, rowId, candidateId]);

    const setChoice = useCallback((field, value) => setChoices((s) => ({ ...s, [field]: value })), []);

    const writes = (comparison?.fields || []).filter((f) => f.would_write && choices[f.field] !== 'crm');
    const kept = (comparison?.fields || []).filter((f) => f.would_write && choices[f.field] === 'crm');
    const chosen = candidates.find((c) => c.id === candidateId);
    const entityWord = row?.entity_type === 'Company' ? 'company' : 'contact';

    const summary = () => {
        if (!decision) return 'Choose a CRM record, Create new, or Skip.';
        if (decision === 'skip') return 'This row will not be imported.';
        if (decision === 'new') return `On Commit: create a new ${entityWord}.`;
        if (!comparison) return `On Commit: update ${chosen?.name || 'the chosen record'}.`;
        const list = writes.map((f) => `${fieldName(f.field, fieldLabels)} → ${show(f.written)}`);
        return writes.length
            ? `On Commit: update ${comparison.target.name} and write ${writes.length} field${writes.length === 1 ? '' : 's'}: ${list.join('; ')}.`
            : `On Commit: link to ${comparison.target.name}; no field changes.`;
    };

    const next = () => { if (index < ids.length - 1) setIndex(index + 1); };

    const apply = async () => {
        if (!decision || busy) return;
        setBusy(true); setError(null);
        try {
            // Keeping a CRM value is a staged-value edit: the staged value becomes
            // the CRM's, so Commit writes nothing for that field.
            if (candidateId && kept.length) {
                const patch = Object.fromEntries(kept.map((f) => [f.field, asStaged(f.crm)]));
                await api.patchRow(rowId, patch);
            }
            const res = await api.bulk('resolve-match', {
                row_ids: [rowId], target: 'record',
                decision: decision === 'new' ? 'new' : decision === 'skip' ? 'skip' : 'update',
                target_id: candidateId,
            });
            const failed = res?.results?.find((r) => !r.ok);
            if (!failed && company) {
                const cres = await api.bulk('resolve-match', {
                    row_ids: [rowId], target: 'company', decision: company === 'new' ? 'new' : 'link',
                    target_id: company === 'new' ? null : company,
                });
                const cfail = cres?.results?.find((r) => !r.ok);
                if (cfail) throw { message: cfail.error?.message || blockLabel(cfail.error?.code) };
            }
            if (failed) {
                const msg = failed.error?.message || blockLabel(failed.error?.code);
                setOutcomes((o) => ({ ...o, [rowId]: msg }));
                setError(msg);
            } else {
                setOutcomes((o) => ({ ...o, [rowId]: 'resolved' }));
                onChanged?.();
                next();
            }
        } catch (err) {
            const msg = err?.message && !err.response ? err.message : lmError(err, 'The decision could not be saved.');
            setOutcomes((o) => ({ ...o, [rowId]: msg }));
            setError(msg);
        } finally {
            setBusy(false);
        }
    };

    const resolvedCount = Object.values(outcomes).filter((v) => v === 'resolved').length;
    const failedCount = Object.values(outcomes).filter((v) => v !== 'resolved').length;
    const last = index === ids.length - 1;
    const name = row ? (`${row.staged_values?.first_name || ''} ${row.staged_values?.last_name || ''}`.trim() || row.staged_values?.company_name) : '';

    return (
        <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose?.()}>
            <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto" data-testid="lm-resolve-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">Resolve matches</DialogTitle>
                    <DialogDescription className="text-[13px] text-muted-foreground">
                        Row {Math.min(index + 1, ids.length)} of {ids.length}. Decisions and kept values change only the staged row; nothing is written to the CRM until Commit.
                    </DialogDescription>
                </DialogHeader>

                {loadError && <p role="alert" className="text-[12.5px] text-red-800 dark:text-bad">{loadError}</p>}
                {!row && !loadError && <div className="py-8 flex justify-center gap-2 text-[13px] text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>}

                {row && (
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[14px] font-semibold text-foreground" data-testid="lm-resolve-row-name">{name}</span>
                            <Pill label={MATCH_LABELS[match.status] || 'Not matched'} />
                            {match.evidence && <span className="text-xs text-muted-foreground">evidence: {evidenceLabel(match.evidence)}</span>}
                        </div>

                        <fieldset className="space-y-1.5" data-testid="lm-resolve-candidates">
                            <legend className="text-[12px] font-semibold tracking-[0.05em] text-muted-foreground mb-1">CRM MATCHES</legend>
                            {candidates.length === 0 && <p className="text-[12.5px] text-muted-foreground">No existing CRM {entityWord} matched this row.</p>}
                            {candidates.map((cand) => (
                                <label key={cand.id} className={`flex items-start gap-2.5 px-3 py-2 rounded-[10px] border cursor-pointer ${decision === cand.id ? 'border-primary bg-muted/50' : 'border-border'}`}>
                                    <input type="radio" name="lm-resolve-decision" className="mt-1" checked={decision === cand.id}
                                           onChange={() => setDecision(cand.id)} />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-[13px] font-medium text-foreground">{cand.name}</span>
                                        <span className="block text-xs text-muted-foreground">{cand.detail || `id ${String(cand.id).slice(0, 8)}`} · {evidenceLabel(cand.evidence)}</span>
                                    </span>
                                </label>
                            ))}
                            {[['new', `Create a new ${entityWord}`], ['skip', 'Skip (do not import)']].map(([key, text]) => (
                                <label key={key} className={`flex items-center gap-2.5 px-3 py-2 rounded-[10px] border cursor-pointer ${decision === key ? 'border-primary bg-muted/50' : 'border-border'}`}>
                                    <input type="radio" name="lm-resolve-decision" checked={decision === key} onChange={() => setDecision(key)} />
                                    <span className="text-[13px] text-foreground">{text}</span>
                                </label>
                            ))}
                        </fieldset>

                        {candidateId && (
                            <div>
                                {!comparison && !compareError && <div className="py-3 flex gap-2 text-[12.5px] text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Comparing…</div>}
                                {compareError && <p role="alert" className="text-[12.5px] text-red-800 dark:text-bad">{compareError}</p>}
                                {comparison && (
                                    <>
                                        <div className="text-[12px] text-muted-foreground mb-1">
                                            Write policy: {comparison.write_policy === 'overwrite' ? 'overwrite differing values' : 'fill blank fields only'}
                                        </div>
                                        <Comparison comparison={comparison} choices={choices} setChoice={setChoice} fieldLabels={fieldLabels} />
                                    </>
                                )}
                            </div>
                        )}

                        {companyMatch && companyMatch.status !== 'new' && companyCandidates.length > 0 && decision !== 'skip' && (
                            <fieldset className="space-y-1.5" data-testid="lm-resolve-company">
                                <legend className="text-[12px] font-semibold tracking-[0.05em] text-muted-foreground mb-1">
                                    COMPANY · {MATCH_LABELS[companyMatch.status] || companyMatch.status}
                                </legend>
                                {companyCandidates.map((cand) => (
                                    <label key={cand.id} className="flex items-center gap-2.5 text-[12.5px] text-foreground">
                                        <input type="radio" name="lm-resolve-company" checked={company === cand.id} onChange={() => setCompany(cand.id)} />
                                        Link to {cand.name} <span className="text-muted-foreground">· {cand.detail || evidenceLabel(cand.evidence)}</span>
                                    </label>
                                ))}
                                <label className="flex items-center gap-2.5 text-[12.5px] text-foreground">
                                    <input type="radio" name="lm-resolve-company" checked={company === 'new'} onChange={() => setCompany('new')} />
                                    Create a new company
                                </label>
                            </fieldset>
                        )}

                        <div className="px-3.5 py-2.5 rounded-[10px] bg-muted/50 border border-border text-[12.5px] text-foreground" data-testid="lm-resolve-summary">
                            {summary()}
                        </div>
                        {error && <p role="alert" className="text-[12.5px] text-red-800 dark:text-bad" data-testid="lm-resolve-error">{error}</p>}
                    </div>
                )}

                <div className="flex items-center gap-2 pt-1">
                    <span className="text-[12px] text-muted-foreground tabular-nums" data-testid="lm-resolve-progress">
                        {resolvedCount > 0 && <span className="inline-flex items-center gap-1 text-emerald-800 dark:text-good mr-3"><CheckCircle2 className="w-3.5 h-3.5" />{resolvedCount} resolved</span>}
                        {failedCount > 0 && <span className="inline-flex items-center gap-1 text-red-800 dark:text-bad"><XCircle className="w-3.5 h-3.5" />{failedCount} need attention</span>}
                    </span>
                    <span className="flex-1" />
                    <GhostButton icon={ChevronLeft} onClick={() => setIndex(Math.max(0, index - 1))} disabled={busy || index === 0}>Back</GhostButton>
                    {!last && <GhostButton onClick={next} disabled={busy}>Leave as is <ChevronRight className="w-3.5 h-3.5 inline" /></GhostButton>}
                    {last && <GhostButton onClick={onClose} disabled={busy}>Done</GhostButton>}
                    <PrimaryButton onClick={apply} disabled={busy || !decision || !row || (candidateId && !comparison)} testId="lm-resolve-apply">
                        {busy ? 'Saving…' : last ? 'Apply' : 'Apply and next'}
                    </PrimaryButton>
                </div>
            </DialogContent>
        </Dialog>
    );
}
